import type {
  QuizConfigV2,
  QuizQuestionV2,
  TravelerDimension,
} from '@/types/profile'

/**
 * VOY-414 PR#263 review: branching logic extracted from travel-quiz.tsx so it
 * can be unit-tested without mounting the full quiz component.
 *
 * A rule has the form:
 *   - "<qid> answer dimensions.<dim> <op> <value>"   (option-dimension based)
 *   - "<qid> == <optionId>"                          (exact match)
 *
 * Any unparseable condition returns false (safe fallback — branches that
 * shouldn't fire stay closed if the config is malformed).
 */
export function evaluateBranchCondition(
  condition: string,
  answers: Record<string, string>,
  config: QuizConfigV2,
): boolean {
  const eqMatch = condition.match(/^(\S+)\s*==\s*(\S+)$/)
  if (eqMatch) {
    const [, qid, expectedOpt] = eqMatch
    return answers[qid] === expectedOpt
  }
  const dimMatch = condition.match(
    /^(\S+)\s+answer\s+dimensions\.(\S+)\s+([<>])\s+(-?\d+(?:\.\d+)?)$/,
  )
  if (!dimMatch) return false
  const [, qid, dim, op, rawVal] = dimMatch
  const optionId = answers[qid]
  if (!optionId) return false

  const allQuestions = collectAllQuestions(config)
  const question = allQuestions.find(q => q.id === qid)
  const option = question?.options.find(o => o.id === optionId)
  const dimValue = option?.dimensions?.[dim as TravelerDimension]
  if (typeof dimValue !== 'number') return false
  const threshold = parseFloat(rawVal)
  return op === '>' ? dimValue > threshold : dimValue < threshold
}

export function collectAllQuestions(config: QuizConfigV2): QuizQuestionV2[] {
  return [
    ...config.tiers.core.questions,
    ...config.tiers.branching.questions,
    ...config.tiers.differentiator.questions,
  ]
}

export function computeActiveQuestions(
  answers: Record<string, string>,
  config: QuizConfigV2,
): QuizQuestionV2[] {
  const active: QuizQuestionV2[] = [...config.tiers.core.questions]
  const added = new Set<string>()
  for (const rule of config.tiers.branching.rules) {
    if (evaluateBranchCondition(rule.condition, answers, config)) {
      for (const qid of rule.questions) {
        if (added.has(qid)) continue
        const q = config.tiers.branching.questions.find(x => x.id === qid)
        if (q) {
          active.push(q)
          added.add(qid)
        }
      }
    }
  }
  active.push(...config.tiers.differentiator.questions)
  return active.sort((a, b) => a.order - b.order)
}
